// ── Supabase Auth layer — raw fetch (no SDK dependency) ───────────────────────
// Same approach as the existing sb helpers. Uses the Supabase Auth REST API
// directly so it works with TS 4.4 and the current raw-fetch project setup.

import { SB_URL, SB_KEY } from "./supabase";

const AUTH = `${SB_URL}/auth/v1`;
const SESSION_KEY = "pronocup_auth_session";

// ── Types ──────────────────────────────────────────────────────────────────────

export type AuthUser = { id: string; is_anonymous?: boolean };
export type Session = { access_token: string; refresh_token: string; user: AuthUser };
export type Provider = "kakao" | "google";

export type Profile = {
  id: string;
  nickname: string;
  avatar_url: string | null;
  is_guest: boolean;
};

// ── Session storage ────────────────────────────────────────────────────────────

export function saveSession(s: Session) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch {}
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch {}
}

// ── OAuth callback — parse hash fragment returned by Supabase ─────────────────

export type OAuthError = { code: string; message: string };

export function parseHashError(): OAuthError | null {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return null;
    const p = new URLSearchParams(hash);
    const error = p.get("error");
    if (!error) return null;
    const message = p.get("error_description") ?? error;
    history.replaceState(null, "", window.location.pathname + window.location.search);
    return { code: error, message };
  } catch { return null; }
}

function parseHashSession(): Session | null {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return null;
    const p = new URLSearchParams(hash);
    const access_token = p.get("access_token");
    const refresh_token = p.get("refresh_token") ?? "";
    if (!access_token) return null;
    // Decode JWT payload to get user id + real anonymity claim
    const payload = JSON.parse(atob(access_token.split(".")[1]));
    const user: AuthUser = { id: payload.sub, is_anonymous: !!payload.is_anonymous };
    const session: Session = { access_token, refresh_token, user };
    saveSession(session);
    history.replaceState(null, "", window.location.pathname + window.location.search);
    return session;
  } catch { return null; }
}

// ── Public API ─────────────────────────────────────────────────────────────────

export function getSession(): Session | null {
  return parseHashSession() ?? loadSession();
}

export async function signInGuest(): Promise<Session | null> {
  try {
    const r = await fetch(`${AUTH}/signup`, {
      method: "POST",
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
      body: "{}",
    });
    if (!r.ok) { console.warn("guest sign-in:", await r.text()); return null; }
    const d = await r.json();
    const session: Session = {
      access_token: d.access_token,
      refresh_token: d.refresh_token ?? "",
      user: { id: d.user?.id ?? d.id, is_anonymous: true },
    };
    saveSession(session);
    return session;
  } catch (e) { console.warn("guest sign-in:", e); return null; }
}

export async function sendEmailOTP(email: string): Promise<boolean> {
  try {
    const r = await fetch(`${AUTH}/otp`, {
      method: "POST",
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ email, create_user: true }),
    });
    if (!r.ok) { console.warn("send OTP:", await r.text()); return false; }
    return true;
  } catch (e) { console.warn("send OTP:", e); return false; }
}

export async function verifyEmailOTP(email: string, token: string): Promise<Session | null> {
  try {
    const r = await fetch(`${AUTH}/verify`, {
      method: "POST",
      headers: { apikey: SB_KEY, "Content-Type": "application/json" },
      body: JSON.stringify({ type: "email", email, token }),
    });
    if (!r.ok) { console.warn("verify OTP:", await r.text()); return null; }
    const d = await r.json();
    if (!d.access_token) return null;
    const session: Session = {
      access_token: d.access_token,
      refresh_token: d.refresh_token ?? "",
      user: { id: d.user?.id, is_anonymous: false },
    };
    saveSession(session);
    return session;
  } catch (e) { console.warn("verify OTP:", e); return null; }
}

// TODO: Naver — no native Supabase provider; implement via custom OAuth Edge Function when needed.
const PROVIDER_SCOPES: Partial<Record<Provider, string>> = {};

// ── Auth flow tracking — sessionStorage so it's tab-isolated across the OAuth redirect ──

export type AuthFlow = "login" | "link";
const AUTH_FLOW_KEY = "pronocup_auth_flow";
const PENDING_GUEST_PROFILE_KEY = "pronocup_pending_guest_profile_id";

export function setAuthFlow(flow: AuthFlow) {
  try { sessionStorage.setItem(AUTH_FLOW_KEY, flow); } catch {}
}

export function popAuthFlow(): AuthFlow | null {
  try {
    const v = sessionStorage.getItem(AUTH_FLOW_KEY) as AuthFlow | null;
    sessionStorage.removeItem(AUTH_FLOW_KEY);
    return v;
  } catch { return null; }
}

// Stores the guest's profile ID before a link redirect so the boot can verify
// that the returned session belongs to the same user (link succeeded).
export function setPendingGuestProfileId(id: string) {
  try { sessionStorage.setItem(PENDING_GUEST_PROFILE_KEY, id); } catch {}
}

export function popPendingGuestProfileId(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_GUEST_PROFILE_KEY);
    sessionStorage.removeItem(PENDING_GUEST_PROFILE_KEY);
    return v;
  } catch { return null; }
}

// ── Login screen: sign in with a social provider (creates or resumes that provider's account) ──

export function signInWithProvider(provider: Provider) {
  // The OAuth return overwrites the stored session — back it up first so an
  // existing guest can be restored if this login lands on the wrong account.
  backupSessionBeforeRedirect();
  setAuthFlow("login");
  const redirectTo = encodeURIComponent(window.location.origin);
  let url = `${AUTH}/authorize?provider=${provider}&redirect_to=${redirectTo}`;
  const scopes = PROVIDER_SCOPES[provider];
  if (scopes) url += `&scopes=${encodeURIComponent(scopes)}`;
  if (provider === "google") url += "&prompt=select_account";
  window.location.href = url;
}

// ── Guest link: attach a social identity to the CURRENT session without creating a new user ──
// Uses skip_http_redirect=true so Supabase returns a JSON { url } instead of a 302 redirect.
// The URL encodes the current user's JWT in state, so Google binds the identity to this account.
// Requires "Allow manual linking" enabled in Supabase Auth dashboard → Auth settings.
// Throws on any failure — no silent fallback to signInWithProvider.
export async function linkIdentityWithProvider(
  provider: Provider,
  accessToken: string,
  guestProfileId: string
): Promise<void> {
  const redirectTo = encodeURIComponent(window.location.origin);
  const url =
    `${AUTH}/user/identities/authorize` +
    `?provider=${provider}&redirect_to=${redirectTo}&skip_http_redirect=true`;

  const r = await fetch(url, {
    method: "GET",
    headers: { apikey: SB_KEY, Authorization: `Bearer ${accessToken}` },
  });

  if (!r.ok) {
    const text = await r.text().catch(() => String(r.status));
    console.error("[Auth] linkIdentityWithProvider failed:", r.status, text);
    throw new Error(`Link failed (${r.status}): ${text}`);
  }

  const d = await r.json().catch(() => null);
  if (!d?.url) {
    console.error("[Auth] linkIdentityWithProvider: no url in response", d);
    throw new Error("Link failed: Supabase did not return a redirect URL");
  }

  // Persist flow metadata in sessionStorage (tab-isolated, survives the redirect)
  setAuthFlow("link");
  setPendingGuestProfileId(guestProfileId);
  // pendingNickname is NOT stored here — the guest's nickname already exists in profiles
  // and must not be applied to the Google account on return.
  window.location.href = d.url;
}

export function signOut() {
  clearSession();
}

// ── Session backup — survives the OAuth login redirect ─────────────────────────
// signInWithProvider overwrites pronocup_auth_session on return (parseHashSession).
// For a guest that overwrite is destructive: an anonymous user has no way to sign
// back in, its tokens were the only credential. We back the session up before the
// redirect so the recovery screen can restore it ("go back to my previous session").

const SESSION_BACKUP_KEY = "pronocup_auth_session_backup";

function backupSessionBeforeRedirect() {
  try {
    const s = loadSession();
    if (s) localStorage.setItem(SESSION_BACKUP_KEY, JSON.stringify(s));
  } catch {}
}

export function loadSessionBackup(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_BACKUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearSessionBackup() {
  try { localStorage.removeItem(SESSION_BACKUP_KEY); } catch {}
}

// Restores the pre-OAuth session as the active one. Returns false if no backup.
export function restoreSessionBackup(): boolean {
  const b = loadSessionBackup();
  if (!b) return false;
  saveSession(b);
  clearSessionBackup();
  return true;
}

// ── Guest profile ID — device-bound identity for anonymous users ───────────────

const GUEST_PROFILE_KEY = "pronocup_guest_profile_id";

export function getGuestProfileId(): string | null {
  try {
    const val = localStorage.getItem(GUEST_PROFILE_KEY);
    console.log(`[GuestAuth] getGuestProfileId key=${GUEST_PROFILE_KEY} →`, val ?? "null");
    return val;
  } catch (e) {
    console.error("[GuestAuth] getGuestProfileId localStorage error:", e);
    return null;
  }
}

export function setGuestProfileId(id: string) {
  try {
    localStorage.setItem(GUEST_PROFILE_KEY, id);
    console.log(`[GuestAuth] setGuestProfileId key=${GUEST_PROFILE_KEY} ← ${id}`);
  } catch (e) {
    console.error("[GuestAuth] setGuestProfileId localStorage error:", e);
  }
}

export function clearGuestProfileId() {
  try {
    localStorage.removeItem(GUEST_PROFILE_KEY);
    console.log(`[GuestAuth] clearGuestProfileId key=${GUEST_PROFILE_KEY} cleared`);
  } catch (e) {
    console.error("[GuestAuth] clearGuestProfileId localStorage error:", e);
  }
}

// ── Pending nickname — sessionStorage so it's tab-isolated and doesn't bleed between tabs ──
// Only used for the "login" flow (new Google user needs to pick a nickname after OAuth).
// The "link" flow does NOT store a pendingNickname — the guest's nickname already exists.

const PENDING_KEY = "pending_nickname";

export function setPendingNickname(n: string) {
  try { sessionStorage.setItem(PENDING_KEY, n); } catch {}
}

export function popPendingNickname(): string | null {
  try {
    const v = sessionStorage.getItem(PENDING_KEY);
    sessionStorage.removeItem(PENDING_KEY);
    return v;
  } catch { return null; }
}

export function genDefaultNickname(): string {
  return "Player" + Math.floor(1000 + Math.random() * 9000);
}

// ── Profiles table — authenticated requests (user JWT, RLS applies) ────────────

function authed(token: string): Record<string, string> {
  return {
    apikey: SB_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  };
}

// Rename the CURRENT user's profile — authenticated with the user JWT so RLS
// (owner-only update) applies. Used by the boot self-heal when profiles.nickname
// diverges from players.name. Returns false on failure (non-fatal: saveName
// already fixed the device locally).
export async function updateProfileNickname(session: Session, nickname: string): Promise<boolean> {
  try {
    const r = await fetch(`${SB_URL}/rest/v1/profiles?id=eq.${session.user.id}`, {
      method: "PATCH",
      headers: { ...authed(session.access_token), Prefer: "return=minimal" },
      body: JSON.stringify({ nickname, updated_at: new Date().toISOString() }),
    });
    if (!r.ok) console.warn("[Auth] updateProfileNickname failed:", r.status, await r.text());
    return r.ok;
  } catch (e) {
    console.warn("[Auth] updateProfileNickname:", e);
    return false;
  }
}

export async function ensureProfile(
  session: Session,
  nickname: string,
  isGuest: boolean,
  // Renommer un profil EXISTANT doit être un acte explicite (NicknameSetup).
  // Jamais depuis le boot/login : un pseudo tapé sur l'écran Login avant un OAuth
  // vers un compte Google existant renommerait ce compte → désynchronise
  // profiles.nickname de players.name (incident aseo.bonjour→capuccino, 07/2026).
  allowRename: boolean = false
): Promise<Profile | null> {
  const caller = isGuest ? "[GuestAuth]" : "[Auth]";
  console.log(`${caller} ensureProfile called`, { userId: session.user.id, nickname, isGuest, allowRename });

  // Safety check: if pronocup_guest_profile_id already exists in localStorage, we should
  // never create a new profile — the existing one must be reused.
  const storedGuestId = isGuest
    ? (() => { try { return localStorage.getItem("pronocup_guest_profile_id"); } catch { return null; } })()
    : null;
  if (storedGuestId && storedGuestId !== session.user.id) {
    console.warn(
      `${caller} ensureProfile MISMATCH — localStorage has guest_profile_id=${storedGuestId} but session.user.id=${session.user.id}. ` +
      "This means a new anonymous user was created despite an existing guest. Proceeding with session user to avoid orphan profiles."
    );
  }

  try {
    // Read existing profile
    const gr = await fetch(
      `${SB_URL}/rest/v1/profiles?id=eq.${session.user.id}&select=*`,
      { headers: authed(session.access_token) }
    );
    if (gr.ok) {
      const rows: Profile[] = await gr.json();
      if (rows[0]) {
        console.log(`${caller} ensureProfile — existing profile found`, { id: rows[0].id, nickname: rows[0].nickname, is_guest: rows[0].is_guest });
        // Self-heal : la source de vérité de l'anonymat est le JWT (isGuest), pas
        // la colonne is_guest qui a pu être figée à une mauvaise valeur lors d'un
        // ancien aller-retour OAuth. On resynchronise nickname ET is_guest.
        const patch: Record<string, unknown> = {};
        if (allowRename && nickname && rows[0].nickname !== nickname) patch.nickname = nickname;
        if (rows[0].is_guest !== isGuest) patch.is_guest = isGuest;
        if (Object.keys(patch).length > 0) {
          console.log(`${caller} ensureProfile — patching existing profile`, patch);
          patch.updated_at = new Date().toISOString();
          await fetch(`${SB_URL}/rest/v1/profiles?id=eq.${session.user.id}`, {
            method: "PATCH",
            headers: { ...authed(session.access_token), Prefer: "return=minimal" },
            body: JSON.stringify(patch),
          });
          return { ...rows[0], ...patch } as Profile;
        }
        console.log(`${caller} ensureProfile — no patch needed, returning existing profile`);
        return rows[0];
      }
    }

    // Create profile — only if a nickname was provided.
    // Callers must show a nickname picker when nickname is empty (avoids "PlayerXXXX" accounts).
    if (!nickname) {
      console.warn(`${caller} ensureProfile — no nickname provided, cannot create profile`);
      return null;
    }
    const nick = nickname;
    const row: Profile = {
      id: session.user.id,
      nickname: nick,
      avatar_url: null,
      is_guest: isGuest,
    };
    console.log(`${caller} ensureProfile — creating NEW profile`, row);
    const cr = await fetch(`${SB_URL}/rest/v1/profiles`, {
      method: "POST",
      headers: {
        ...authed(session.access_token),
        Prefer: "resolution=ignore-duplicates,return=representation",
      },
      body: JSON.stringify(row),
    });
    // Vérifier que la ligne a RÉELLEMENT persisté. Un POST silencieusement perdu
    // (RLS, contrainte, ignore-duplicates) laissait un user Google sans profil
    // pour toujours → incidents d'identité orphelines de 07/2026 (SJ, camille, Luka).
    let created: Profile | null = null;
    if (cr.ok) {
      const arr = await cr.json().catch(() => null);
      if (Array.isArray(arr) && arr[0]) created = arr[0] as Profile;
    } else {
      console.error(`${caller} ensureProfile — profile INSERT failed:`, cr.status, await cr.text().catch(() => ""));
    }
    if (!created) {
      // Re-lecture à travers la RLS : un autre onglet a pu créer le profil entre-temps.
      const rr = await fetch(
        `${SB_URL}/rest/v1/profiles?id=eq.${session.user.id}&select=*`,
        { headers: authed(session.access_token) }
      );
      const rows2: Profile[] = rr.ok ? await rr.json() : [];
      created = rows2[0] ?? null;
    }
    if (!created) {
      console.error(`${caller} ensureProfile — profile did NOT persist for id=${session.user.id}`);
      return null;
    }
    console.log(`${caller} ensureProfile — profile created with id=${session.user.id}`);
    return created;
  } catch (e) {
    console.error(`${caller} ensureProfile error:`, e);
    return null;
  }
}
